import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  type ResizeHandle,
  useResizable,
  type UseResizableOptions,
} from "../../hooks/useResizable";
import { cn } from "../../lib/utils";

/**
 * Imperative handle for ResizablePanel. Exposes setWidth/setHeight for programmatic
 * resize and resetWidth/resetHeight to restore default size (e.g. collapse after expand).
 */
export interface ResizablePanelHandle {
  setWidth: (width: number) => void;
  setHeight?: (height: number) => void;
  resetWidth: () => void;
  resetHeight?: () => void;
}

const HANDLE_CURSORS: Record<ResizeHandle, string> = {
  left: "col-resize",
  right: "col-resize",
  top: "row-resize",
  bottom: "row-resize",
  "top-left": "nwse-resize",
  "top-right": "nesw-resize",
  "bottom-left": "nesw-resize",
  "bottom-right": "nwse-resize",
};

const CORNER_HANDLES: ResizeHandle[] = [
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
];

/** Matches WorkspaceAppHeader nav zone: Tailwind `duration-300 ease-out` / --ease-out. */
const PANEL_OPEN_CLOSE_DURATION_S = 0.3;
const PANEL_OPEN_CLOSE_EASE_CSS = "cubic-bezier(0.2, 0, 0, 1)";

function hasHorizontalHandle(handles: ResizeHandle[]): boolean {
  return handles.some(
    (h) => h === "left" || h === "right" || CORNER_HANDLES.includes(h),
  );
}

function hasVerticalHandle(handles: ResizeHandle[]): boolean {
  return handles.some(
    (h) => h === "top" || h === "bottom" || CORNER_HANDLES.includes(h),
  );
}

/** Which axis collapses when `isClosed` — height-only panels collapse height, else width. */
function getCloseAxis(handles: ResizeHandle[]): "width" | "height" {
  if (hasVerticalHandle(handles) && !hasHorizontalHandle(handles)) {
    return "height";
  }
  return "width";
}

function isCorner(handle: ResizeHandle): boolean {
  return CORNER_HANDLES.includes(handle);
}

function getHandleAriaLabel(
  handle: ResizeHandle,
  defaultLabel: string,
): string {
  if (handle === "left" || handle === "right") return defaultLabel;
  if (handle === "top") return "Resize from top";
  if (handle === "bottom") return "Resize from bottom";
  return `Resize from ${handle.replace("-", " ")} corner`;
}

function getHandleAriaOrientation(
  handle: ResizeHandle,
): "horizontal" | "vertical" | undefined {
  if (handle === "left" || handle === "right") return "vertical";
  if (handle === "top" || handle === "bottom") return "horizontal";
  return undefined;
}

export interface ResizablePanelProps
  extends Omit<UseResizableOptions, "onWidthChange" | "onHeightChange"> {
  isClosed?: boolean;
  /**
   * Duration of the open/close animation in seconds.
   * @default 0.3 — matches WorkspaceAppHeader nav zone `duration-300`
   */
  transitionDuration?: number;
  /**
   * The content to be displayed in the resizable panel
   */
  children: React.ReactNode;
  /**
   * Custom className for the panel container
   */
  className?: string;
  /**
   * Custom styles for the panel container
   */
  style?: React.CSSProperties;
  /**
   * Custom className for the resize handle
   */
  handleClassName?: string;
  /**
   * Custom className for the resize handle indicator
   */
  handleIndicatorClassName?: string;
  /**
   * Whether to show the resize handle indicator pill (edges only; corners show a small grip)
   * @default true
   */
  showHandleIndicator?: boolean;
  /**
   * Callback fired when width changes (after drag completes or programmatic set)
   */
  onWidthChange?: (width: number) => void;
  /**
   * Callback fired while the user drags a width handle (live updates during resize)
   */
  onWidthChangeEvent?: (width: number) => void;
  /**
   * Callback fired when the user begins dragging a resize handle
   */
  onResizeStart?: () => void;
  /**
   * Callback fired when the user finishes dragging a resize handle
   */
  onResizeEnd?: () => void;
  /**
   * Callback fired when height changes (when using top/bottom or corner handles)
   */
  onHeightChange?: (height: number) => void;
  /**
   * ARIA label for the resize handle (used for left/right edges; others have built-in labels)
   * @default "Resize panel"
   */
  resizeAriaLabel?: string;
  /**
   * Whether to apply a border to the panel
   * @default false
   */
  bordered?: boolean;
  /**
   * One or more resize handle positions. When provided, overrides direction.
   * e.g. ["right", "bottom", "bottom-right"] for right edge, bottom edge, and corner.
   */
  directions?: ResizeHandle[];
}

function needsHeightOptions(handles: ResizeHandle[]): boolean {
  return handles.some(
    (h) => h === "top" || h === "bottom" || CORNER_HANDLES.includes(h),
  );
}

/**
 * ResizablePanel component provides a container with drag-to-resize functionality.
 * Users can drag handles on edges (left, right, top, bottom) or corners to resize.
 *
 * @example
 * ```tsx
 * <ResizablePanel direction="right" minWidth={300} maxWidth={800}>
 *   <div>Your content here</div>
 * </ResizablePanel>
 *
 * <ResizablePanel directions={["right", "bottom", "bottom-right"]} initialHeight={400}>
 *   <div>Resizable from right, bottom, and corner</div>
 * </ResizablePanel>
 * ```
 */
export const ResizablePanel = forwardRef<
  ResizablePanelHandle,
  ResizablePanelProps
>(function ResizablePanel(
  {
    isClosed,
    transitionDuration = PANEL_OPEN_CLOSE_DURATION_S,
    children,
    className,
    style,
    handleClassName,
    handleIndicatorClassName,
    showHandleIndicator = true,
    onWidthChange,
    onWidthChangeEvent,
    onResizeStart,
    onResizeEnd,
    onHeightChange,
    resizeAriaLabel = "Resize panel",
    bordered = false,
    direction = "right",
    directions,
    ...resizableOptions
  },
  ref,
) {
  const handles: ResizeHandle[] =
    directions ?? (direction != null ? [direction] : ["right"]);
  const withHeight = needsHeightOptions(handles);
  const closeAxis = getCloseAxis(handles);
  const closesWidth = isClosed && closeAxis === "width";
  const closesHeight = isClosed && closeAxis === "height";

  const hookOptions: UseResizableOptions = {
    ...resizableOptions,
    onWidthChange,
    onWidthChangeEvent,
    onResizeStart,
    onResizeEnd,
    onHeightChange: withHeight ? onHeightChange : undefined,
    initialHeight: withHeight
      ? (resizableOptions.initialHeight ?? 400)
      : undefined,
    minHeight: withHeight ? (resizableOptions.minHeight ?? 200) : undefined,
    maxHeight: withHeight ? resizableOptions.maxHeight : undefined,
    renderedWidth: closesWidth ? 0 : resizableOptions.renderedWidth,
    renderedHeight: closesHeight && withHeight ? 0 : undefined,
  };

  const {
    width,
    height,
    isDragging,
    elementRef,
    handleMouseDown,
    setWidth,
    setHeight,
    resetWidth,
    resetHeight,
  } = useResizable(hookOptions);

  useImperativeHandle(
    ref,
    () => ({
      setWidth,
      resetWidth,
      ...(setHeight !== undefined ? { setHeight } : {}),
      ...(resetHeight !== undefined ? { resetHeight } : {}),
    }),
    [setWidth, setHeight, resetWidth, resetHeight],
  );

  const [isOpenCloseAnimating, setIsOpenCloseAnimating] = useState(false);
  const prevIsClosedRef = useRef(isClosed);
  const openCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (prevIsClosedRef.current === isClosed) return;
    prevIsClosedRef.current = isClosed;

    setIsOpenCloseAnimating(true);
    if (openCloseTimerRef.current) {
      clearTimeout(openCloseTimerRef.current);
    }
    openCloseTimerRef.current = setTimeout(() => {
      setIsOpenCloseAnimating(false);
      openCloseTimerRef.current = null;
    }, transitionDuration * 1000);

    return () => {
      if (openCloseTimerRef.current) {
        clearTimeout(openCloseTimerRef.current);
        openCloseTimerRef.current = null;
      }
    };
  }, [isClosed, transitionDuration]);

  const sizeTransition = `${transitionDuration}s ${PANEL_OPEN_CLOSE_EASE_CSS}`;
  const transition = isDragging
    ? "none"
    : withHeight
      ? `width ${sizeTransition}, height ${sizeTransition}`
      : `width ${sizeTransition}`;

  // The width the panel shows (or targets) while open: the caller's
  // renderedWidth override wins over the stored/dragged width.
  const openWidth = resizableOptions.renderedWidth ?? width;
  const effectiveWidth = closesWidth ? 0 : openWidth;

  const panelStyle: React.CSSProperties = {
    width: `${effectiveWidth}px`,
    ...(height !== undefined
      ? { height: `${closesHeight ? 0 : height}px` }
      : {}),
    ...style,
    transition,
  };

  const pinContentWidth =
    !isDragging && closeAxis === "width" && (isClosed || isOpenCloseAnimating);
  const pinContentHeight =
    !isDragging &&
    withHeight &&
    closeAxis === "height" &&
    (isClosed || isOpenCloseAnimating) &&
    height !== undefined;

  // Pin to the open target width (not the stored width) so a caller-supplied
  // renderedWidth applied mid-animation doesn't leave content at the old size.
  const contentSizeStyle: React.CSSProperties | undefined = pinContentWidth
    ? { width: `${openWidth}px` }
    : pinContentHeight
      ? { height: `${height}px` }
      : undefined;

  // Handles must wait for the open animation — drag math seeds from offsetWidth,
  // which still animates from 0 while isClosed has already become false.
  const showResizeHandles =
    !resizableOptions.disabled && !isClosed && !isOpenCloseAnimating;

  return (
    <div
      ref={elementRef}
      className={cn(
        "relative shrink-0 overflow-hidden",
        bordered && "border",
        className,
      )}
      style={panelStyle}
      aria-hidden={isClosed || undefined}
    >
      {showResizeHandles &&
        handles.map((handle) => {
          const cursor = HANDLE_CURSORS[handle];
          const corner = isCorner(handle);
          const ariaOrientation = getHandleAriaOrientation(handle);

          if (corner) {
            const cornerPosition: Record<
              "top-left" | "top-right" | "bottom-left" | "bottom-right",
              string
            > = {
              "top-left": "top-0 left-0",
              "top-right": "top-0 right-0",
              "bottom-left": "bottom-0 left-0",
              "bottom-right": "bottom-0 right-0",
            };
            const positionClasses =
              cornerPosition[handle as keyof typeof cornerPosition];
            return (
              <div
                key={handle}
                className={cn(
                  "absolute z-50 h-3 w-3 bg-transparent hover:bg-border/50 transition-colors rounded-sm",
                  positionClasses,
                  isDragging && "bg-border",
                  handleClassName,
                  !showHandleIndicator && "opacity-0",
                )}
                style={{ cursor }}
                onMouseDown={(e) => handleMouseDown(e, handle)}
                role="separator"
                aria-label={getHandleAriaLabel(handle, resizeAriaLabel)}
                tabIndex={0}
              />
            );
          }

          const isVertical = handle === "left" || handle === "right";
          const edgePosition: Record<
            "left" | "right" | "top" | "bottom",
            string
          > = {
            left: "left-0 top-0 h-full w-[4px] -ml-[2px]",
            right: "right-0 top-0 h-full w-[4px] -mr-[2px]",
            top: "top-0 left-0 w-full h-[4px] -mt-[2px]",
            bottom: "bottom-0 left-0 w-full h-[4px] -mb-[2px]",
          };
          const positionClasses =
            edgePosition[handle as keyof typeof edgePosition];

          return (
            <div
              key={handle}
              className={cn(
                "absolute bg-transparent hover:bg-border/50 transition-colors group z-50",
                positionClasses,
                isDragging && "bg-border",
                handleClassName,
                !showHandleIndicator && "opacity-0",
              )}
              style={{ cursor }}
              onMouseDown={(e) => handleMouseDown(e, handle)}
              role="separator"
              aria-label={getHandleAriaLabel(handle, resizeAriaLabel)}
              aria-orientation={ariaOrientation}
              tabIndex={0}
              onKeyDown={(e) => {
                if (
                  (isVertical &&
                    (e.key === "ArrowLeft" || e.key === "ArrowRight")) ||
                  (!isVertical &&
                    (e.key === "ArrowUp" || e.key === "ArrowDown"))
                ) {
                  e.preventDefault();
                }
              }}
            >
              {showHandleIndicator && (
                <div
                  aria-hidden="true"
                  className={cn(
                    "pointer-events-none absolute rounded-full border bg-background group-hover:bg-secondary transition-colors",
                    isVertical
                      ? "top-1/2 -translate-y-1/2 h-6 w-2 translate-x-[-2px]"
                      : "left-1/2 -translate-x-1/2 w-6 h-2 translate-y-[-2px]",
                    isDragging && "bg-border",
                    handleIndicatorClassName,
                  )}
                />
              )}
            </div>
          );
        })}

      {/* Content — pinned to stored open size during open/close so text clips instead of reflowing */}
      <div
        className={cn("h-full w-full overflow-hidden", {
          "shrink-0": pinContentWidth || pinContentHeight,
          "pointer-events-none": isDragging || isClosed,
        })}
        style={contentSizeStyle}
      >
        {children}
      </div>
    </div>
  );
});

export default ResizablePanel;
